import { DMSLocation } from "../model/DMSLocation";

/**
 * 10進法 -> 60進法(DMS) 変換関数
 * (計算ロジック自体は変わりません)
 */
export function convertDecimalToDMS(decimal: number): DMSLocation {
  const absoluteValue = Math.abs(decimal);
  const degrees = Math.floor(absoluteValue);
  const minutesFull = (absoluteValue - degrees) * 60;
  const minutes = Math.floor(minutesFull);
  const seconds = (minutesFull - minutes) * 60;

  return {
    degrees: decimal < 0 ? -degrees : degrees,
    minutes: minutes,
    seconds: seconds
  };
}

/**
 * DMSオブジェクトを文字列にフォーマットする関数 (改良版)
 * 分と秒の整数部分を必ず2桁になるよう0埋めします。
 * * @param dms DMSオブジェクト
 * @param precision 秒の小数点以下の桁数 (デフォルト2)
 */
export function formatDMS(dms: DMSLocation, precision: number = 2): string {
  // 1. 分を2桁の文字列に変換 (例: 5 -> "05", 15 -> "15")
  const mm = dms.minutes.toString().padStart(2, '0');

  // 2. 秒の処理
  // まず指定の精度で文字列化 (例: 9.123 -> "9.12")
  const secString = dms.seconds.toFixed(precision);
  
  // 整数部と小数部に分割して、整数部だけ0埋めする
  const [secInt, secDec] = secString.split('.');
  const secIntPadded = secInt.padStart(2, '0');

  // 小数点以下がある場合とない場合で結合 (例: "09" + "." + "12" -> "09.12")
  const ss = secDec ? `${secIntPadded}.${secDec}` : secIntPadded;
  
  return `${dms.degrees}°${mm}'${ss}"`;
}

/**
 * 緯度表示フォーマットオプション
 */
export interface DisplayLocationOptions {
  /**
   * 日本語表記にするかどうか (true: 北緯/南緯, false: N/S)
   * @default false
   */
  isJapanese?: boolean;
}

/**
 * 緯度をフォーマットする関数
 * @param val 緯度の数値(10進数) または DMSオブジェクト
 * @param options オプション
 */
export function formatLatitude(val: number | DMSLocation, options?: DisplayLocationOptions): string {
  let isNegative = false;
  let dms: DMSLocation;

  if (typeof val === 'number') {
    isNegative = val < 0;
    dms = convertDecimalToDMS(val);
  } else {
    // 負の値または -0 を判定
    isNegative = val.degrees < 0 || (val.degrees === 0 && 1 / val.degrees === -Infinity);
    dms = val;
  }

  // 表示用に絶対値化
  const absDms = { ...dms, degrees: Math.abs(dms.degrees) };
  const dmsString = formatDMS(absDms);

  if (options?.isJapanese) {
    return (isNegative ? '南緯' : '北緯') + dmsString;
  }
  return dmsString + (isNegative ? 'S' : 'N');
}

/**
 * 経度をフォーマットする関数
 * @param val 経度の数値(10進数) または DMSオブジェクト
 * @param options オプション
 */
export function formatLongitude(val: number | DMSLocation, options?: DisplayLocationOptions): string {
  let isNegative = false;
  let dms: DMSLocation;

  if (typeof val === 'number') {
    isNegative = val < 0;
    dms = convertDecimalToDMS(val);
  } else {
    // 負の値または -0 を判定
    isNegative = val.degrees < 0 || (val.degrees === 0 && 1 / val.degrees === -Infinity);
    dms = val;
  }

  // 表示用に絶対値化
  const absDms = { ...dms, degrees: Math.abs(dms.degrees) };
  const dmsString = formatDMS(absDms);

  if (options?.isJapanese) {
    return (isNegative ? '西経' : '東経') + dmsString;
  }
  return dmsString + (isNegative ? 'W' : 'E');
}
